import { ArrowUpRight, MessageCircle } from "lucide-react";
import { PageHero, Reveal, SectionHead, Eyebrow } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { SERVICE_GROUPS, COMPLEMENTA, waKarina, wa, WA_CARMELINA, WA_VALORACION } from "@/lib/site";

const ServiceRow = ({ s, waTarget, dark = false }) => (
  <Reveal>
    <div
      className={`group grid lg:grid-cols-12 gap-4 lg:gap-8 py-8 lg:py-10 border-t ${dark ? "border-cream/15" : "border-ink/15"}`}
      data-testid={`service-${s.id}`}
    >
      <h3 className={`font-serif text-2xl lg:text-3xl lg:col-span-4 ${dark ? "text-cream" : "text-ink"} group-hover:text-gold transition-colors duration-300`}>
        {s.name}
      </h3>
      <div className="lg:col-span-5">
        <p className={`text-sm leading-relaxed ${dark ? "text-cream/60" : "text-ink/65"}`}>{s.desc}</p>
        {s.photos && (
          <div className="flex flex-wrap gap-3 mt-5" data-testid={`service-photos-${s.id}`}>
            {s.photos.map((p, i) => (
              <img
                key={p}
                src={p}
                alt={`${s.name} — trabajo real`}
                data-testid={`service-photo-${s.id}-${i}`}
                loading="lazy"
                onClick={() => window.open(waTarget, "_blank")}
                className="h-28 lg:h-40 w-auto object-contain border border-gold/30 hover:scale-[1.04] hover:border-gold transition-all duration-500 cursor-pointer"
              />
            ))}
          </div>
        )}
      </div>
      <div className="lg:col-span-3 flex lg:flex-col lg:items-end justify-between gap-4">
        <span className="font-mono text-sm text-gold tracking-wide" data-testid={`service-price-${s.id}`}>
          {s.price || "Consulta disponibilidad"}
        </span>
        <a
          href={waTarget}
          target="_blank"
          rel="noreferrer"
          data-testid={`service-book-${s.id}`}
          className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${
            dark
              ? "border-gold/50 text-gold hover:bg-gold hover:text-ink"
              : "border-ink/30 text-ink hover:border-gold hover:text-gold"
          }`}
        >
          Reservar <ArrowUpRight size={12} />
        </a>
      </div>
    </div>
  </Reveal>
);

const Servicios = () => (
  <div data-testid="servicios-page">
    <PageHero
      eyebrow="Servicios"
      lines={["Cada servicio,", "una decisión con criterio."]}
      sub="Todos los servicios de micropigmentación (NAGI Brows / NAGI Lips) los realiza personalmente Karina Méndez, creadora de la Técnica NAGI."
    />

    <section className="bg-ink text-cream px-6 sm:px-12 lg:px-20 py-16 lg:py-24 border-y border-gold/25" data-testid="servicios-banner">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <Eyebrow dark>Bienvenida</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-5">
            Tu primera cita es <span className="italic text-gold">gratis.</span>
          </h2>
          <p className="mt-6 text-cream/70 leading-relaxed max-w-md">
            Antes de dar el paso, valoramos juntas tu rostro, tu piel y lo que realmente te favorece — sin
            compromiso. Así decides con total seguridad si la Técnica NAGI es para ti.
          </p>
          <a
            href={WA_VALORACION}
            target="_blank"
            rel="noreferrer"
            data-testid="servicios-valoracion-cta"
            className="inline-flex items-center gap-3 mt-8 rounded-full bg-gold text-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] hover:bg-cream transition-colors duration-300"
          >
            <MessageCircle size={16} /> Reserva tu valoración gratuita
          </a>
        </div>
        <Reveal delay={0.15} className="justify-self-center lg:justify-self-end">
          <div className="relative">
            <div className="absolute -inset-3 border border-gold/40 pointer-events-none" />
            <img
              src="/images/karina-trabajando.webp"
              alt="Karina Méndez realizando una valoración de cejas"
              data-testid="servicios-banner-image"
              className="max-h-[340px] lg:max-h-[420px] w-auto object-contain"
            />
          </div>
        </Reveal>
      </div>
    </section>

    {SERVICE_GROUPS.map((group, gi) => (
      <section
        key={group.id}
        className={`${gi % 2 === 1 ? "bg-beige" : "bg-cream"} px-6 sm:px-12 lg:px-20 py-16 lg:py-24`}
        data-testid={`servicios-${group.id}`}
      >
        <SectionHead num={`0${gi + 1}`} eyebrow="Karina Méndez Studio" title={group.title} />
        <div className="mt-10">
          {group.items.map((s) => (
            <ServiceRow key={s.id} s={s} waTarget={waKarina(s.name)} />
          ))}
        </div>
      </section>
    ))}

    <Marquee dark={false} />

    <section className="bg-ink text-cream px-6 sm:px-12 lg:px-20 py-16 lg:py-28" data-testid="servicios-complementa">
      <SectionHead
        dark
        num="04"
        eyebrow="Complementa tu experiencia"
        title={<>Bienestar y cuidado <span className="italic text-gold">corporal</span></>}
      />
      <Reveal className="mt-6 max-w-xl">
        <p className="text-sm text-cream/60 leading-relaxed">
          Junto a mis servicios, cuento con la colaboración de una profesional dedicada al bienestar y cuidado
          corporal.
        </p>
      </Reveal>
      <div className="mt-10">
        {COMPLEMENTA.map((s) => (
          <ServiceRow
            key={s.id}
            s={s}
            dark
            waTarget={wa(WA_CARMELINA, `Hola Carmelina, quiero consultar disponibilidad para ${s.name}.`)}
          />
        ))}
      </div>
      <Reveal className="mt-10">
        <a
          href={wa(WA_CARMELINA, "Hola Carmelina, quiero consultar disponibilidad.")}
          target="_blank"
          rel="noreferrer"
          data-testid="complementa-carmelina-cta"
          className="inline-flex items-center gap-3 rounded-full border border-gold/50 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-gold hover:bg-gold hover:text-ink transition-colors duration-300"
        >
          <MessageCircle size={15} /> Consultar por WhatsApp — Carmelina
        </a>
      </Reveal>
    </section>

    <section className="bg-beige px-6 sm:px-12 lg:px-20 py-12" data-testid="servicios-politica">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60 leading-loose max-w-3xl">
        Nota — Valoración gratuita · Reserva con pago anticipado sin devolución · Precios en formato “desde”,
        el presupuesto final se confirma en tu valoración · Sin sistemas de citas de terceros: cada cita se
        confirma personalmente por WhatsApp.
      </p>
    </section>
  </div>
);

export default Servicios;
